import { api } from './api';

export interface StaffAttendance {
  employeeId: string; date: string; status: string; source: string;
  overriddenFrom: string | null; checkInAt: string | null; checkOutAt: string | null; note: string | null;
}
export interface StatutoryRule {
  id: string; kind: string; label: string; basis: string; value: number;
  wageFloorPaise: number; wageCeilingPaise: number; isEmployerContribution: boolean;
  effectiveFrom: string; effectiveTo: string | null;
  isVerified: boolean; verifiedBy: string | null; verifiedAt: string | null; sourceNote: string | null;
}
export interface SalaryComponentDto { label: string; kind: string; amountPaise: number; countsForStatutoryBase: boolean; }
export interface SalaryStructure { id: string; employeeId: string; effectiveFrom: string; grossPaise: number; statutoryBasePaise: number; components: SalaryComponentDto[]; }
export interface PayslipLine { label: string; amountPaise: number; isEmployerContribution: boolean; }
export interface Payslip {
  employeeId: string; grossPaise: number; employeeDeductionsPaise: number;
  employerContributionsPaise: number; voluntaryDeductionsPaise: number; netPaise: number; lines: PayslipLine[];
}
export interface PayrollRun {
  id: string; year: number; month: number; status: string; totalNetPaise: number;
  finalizedAt: string | null; payslips: Payslip[]; unverifiedRules: string[];
}

export const attendanceApi = {
  checkIn: () => api.post<StaffAttendance>('/api/hr/me/attendance/check-in'),
  checkOut: () => api.post<StaffAttendance>('/api/hr/me/attendance/check-out'),
  mine: () => api.get<StaffAttendance[]>('/api/hr/me/attendance/'),
  register: (date?: string) => api.get<StaffAttendance[]>(`/api/hr/attendance/${date ? `?date=${date}` : ''}`),
  mark: (b: { employeeId: string; date: string; status: string; note?: string | null }) =>
    api.post<StaffAttendance>('/api/hr/attendance/mark', b),
};

export interface PaymentProblem { employeeId: string; fullName: string; what: string }
export interface PaymentFilePreview {
  payable: number; totalPaise: number; canExport: boolean;
  refusal: string | null; problems: PaymentProblem[];
}

export interface LopDivisorOption { value: string; label: string }
export interface LopSettings { divisor: string; label: string; chosen: boolean; options: LopDivisorOption[] }

export const payrollApi = {
  lopSettings: () => api.get<LopSettings>('/api/hr/payroll/lop-settings'),
  setLopDivisor: (divisor: string) => api.put<LopSettings>('/api/hr/payroll/lop-settings', { divisor }),

  listRules: () => api.get<StatutoryRule[]>('/api/hr/payroll/rules'),
  createRule: (b: Record<string, unknown>) => api.post<StatutoryRule>('/api/hr/payroll/rules', b),
  verifyRule: (id: string, verifiedBy: string, sourceNote?: string) =>
    api.post<StatutoryRule>(`/api/hr/payroll/rules/${id}/verify`, { verifiedBy, sourceNote: sourceNote ?? null }),

  listStructures: () => api.get<SalaryStructure[]>('/api/hr/payroll/structures'),
  setStructure: (b: { employeeId: string; effectiveFrom: string; components: SalaryComponentDto[] }) =>
    api.put<SalaryStructure>('/api/hr/payroll/structures', b),

  listRuns: () => api.get<PayrollRun[]>('/api/hr/payroll/runs'),
  getRun: (id: string) => api.get<PayrollRun>(`/api/hr/payroll/runs/${id}`),
  compute: (year: number, month: number) => api.post<PayrollRun>('/api/hr/payroll/runs/compute', { year, month }),
  /* ⚠ ALWAYS CALLED BEFORE THE DOWNLOAD IS OFFERED. A bad IFSC found by the bank costs a day; the
     same fact found here costs a minute. */
  paymentFilePreview: (id: string) =>
    api.get<PaymentFilePreview>(`/api/hr/payroll/runs/${id}/payment-file/preview`),

  /* ⚠ Returns a URL rather than fetching: the browser's own download does the right thing with the
     filename the server sets, and a CSV pulled into JS and re-encoded is where BOM and newline
     problems come from. */
  paymentFileUrl: (id: string, q: { format: string; rail: string; debitAccount: string; valueDate: string; narration?: string }) =>
    `/api/hr/payroll/runs/${id}/payment-file?format=${encodeURIComponent(q.format)}`
    + `&rail=${encodeURIComponent(q.rail)}&debitAccount=${encodeURIComponent(q.debitAccount)}`
    + `&valueDate=${encodeURIComponent(q.valueDate)}`
    + (q.narration ? `&narration=${encodeURIComponent(q.narration)}` : ''),

  finalize: (id: string, note?: string) => api.post<PayrollRun>(`/api/hr/payroll/runs/${id}/finalize`, { note: note ?? null }),
};

/**
 * Paise to rupees, for a payroll screen.
 *
 * ⚠ A MISSING FIGURE RENDERS AS "—", NOT AS "₹NaN". Found by rendering a payslip whose deductions
 * field the server had not sent: the column read ₹NaN, on a screen a principal reads to decide
 * whether to pay forty people. NaN propagates silently through addition, so ONE absent field turns
 * a whole column into nonsense — and the number beside it still looks authoritative.
 *
 * ⚠ AND A DASH IS THE HONEST OUTPUT, NOT A ZERO. "₹0.00" states that nothing was deducted, which is
 * a claim; "—" states that the figure is not known, which is the truth.
 */
export const rupees = (p: number | null | undefined) =>
  p === null || p === undefined || !Number.isFinite(p)
    ? '—'
    : '₹' + (p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
